/**
 * Step to create account for Clever Library users.
 */
import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useEffect, useState} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    Body,
    HeadingMedium,
    LabelLarge,
} from "@khanacademy/wonder-blocks-typography";
import {isEmailValid} from "@khan/auth-password";
import {calculateNextUrl} from "@khan/auth-shared";
import {redirect, useLocation} from "@khan/urls";

// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import DeprecatedFormWrappedTextField from "~/components/text-inputs-package/deprecated-form-wrapped-text-field.tsx";
import {useApolloGqlClient} from "~/data-access/gql/use-apollo-gql-client.ts";

import {signUpWithClever} from "./clever-library-signup.ts";

// Error string from `/login/clever/email` POST request when an email was
// provided for an account that already exists.
const EXISTING_ACCOUNT_FOUND = "EXISTING_ACCOUNT_FOUND";

interface Props {
    cleverId: string;
    isTeacher: boolean;
    cleverEmail?: string;
    dateOfBirth?: string;
    transferToken?: string;
    onChooseExistingAccount: (data: {defaultIdentifier: string}) => void;
    onCreateAccountSubmit: () => void;
    onCreateAccountError: () => void;
    /**
     * Optional identifier to use for the dialog title.
     *
     * Usefult for announcing the dialog to Screen Readers, as it will be used
     * as the `aria-labelledby` attribute of the dialog.
     */
    titleId?: string;
}

export default function CreateAccountStep({
    cleverEmail,
    dateOfBirth,
    transferToken,
    onCreateAccountSubmit,
    onCreateAccountError,
    onChooseExistingAccount,
    titleId,
}: Props) {
    const {gqlClient} = useApolloGqlClient();
    const location = useLocation();
    const [mounted, setMounted] = useState(false);
    const [state, setState] = useState({
        email: cleverEmail || "",
        emailIsValid: !!isEmailValid(cleverEmail || ""),
        foundExistingAccount: true,
        error: null as string | null,
        submitting: false,
    });

    useEffect(() => {
        setMounted(true);
        return () => setMounted(false);
    }, []);

    const handleChange = (e: React.SyntheticEvent) => {
        if (e.target instanceof HTMLInputElement) {
            const email = e.target.value;
            setState((prev) => ({
                ...prev,
                email,
                emailIsValid: !!isEmailValid(email),
            }));
        }
    };

    const handleError = (error?: string) => {
        onCreateAccountError();
        if (mounted) {
            if (error === EXISTING_ACCOUNT_FOUND) {
                setState((prev) => ({
                    ...prev,
                    foundExistingAccount: true,
                    submitting: false,
                    error: t`A Khan Academy account for this email already exists. Please log in to this existing account, or enter a new email to continue.`,
                }));
            } else {
                setState((prev) => ({
                    ...prev,
                    submitting: false,
                    error: t`Sorry, we saw an unexpected error! Please try again later.`,
                }));
            }
        }
    };

    const signUp = () => {
        const options = {
            transferToken: transferToken || "",
            email: state.email,
            birthdate: dateOfBirth,
        } as const;

        onCreateAccountSubmit();
        setState((prev) => ({...prev, submitting: true}));

        signUpWithClever(gqlClient, options)
            .then((result) => {
                if (result.user) {
                    calculateNextUrl({
                        gqlClient,
                        location,
                        userData: result.user,
                        loginType: "clever_library",
                        continueUrl: "/",
                        isFirstLogin: true,
                    }).then((nextUrl) => {
                        redirect(nextUrl);
                    });
                } else {
                    handleError(result.error);
                }
            })
            .catch((err) => {
                onCreateAccountError();
                if (mounted) {
                    setState((prev) => ({
                        ...prev,
                        submitting: false,
                        error: t`Sorry, we saw an unexpected error! Please try again later.`,
                    }));
                }
            });
    };

    return (
        <View>
            <Strut size={spacing.medium_16} />
            <HeadingMedium
                id={titleId}
            >{t`Create a new account`}</HeadingMedium>
            <Strut size={spacing.small_12} />
            <LabelLarge tag="label">{t`School email`}</LabelLarge>
            <Strut size={spacing.xSmall_8} />
            <DeprecatedFormWrappedTextField
                type="text"
                placeholder={t`example@school.edu`}
                value={state.email}
                width="100%"
                onChange={handleChange}
            />
            <Strut size={spacing.medium_16} />
            <Button
                disabled={!state.emailIsValid || state.submitting}
                spinner={state.submitting}
                onClick={signUp}
            >
                {t`Sign up`}
            </Button>
            <Strut size={spacing.small_12} />
            {state.error ? (
                <View>
                    <Body style={styles.error}>{state.error}</Body>
                    {state.foundExistingAccount && (
                        <Button
                            kind="tertiary"
                            onClick={() => {
                                onChooseExistingAccount({
                                    defaultIdentifier: state.email,
                                });
                            }}
                        >
                            {t`Log in to existing account`}
                        </Button>
                    )}
                    <Strut size={spacing.large_24} />
                </View>
            ) : (
                <Strut size={spacing.xxLarge_48} />
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    error: {
        color: color.red,
    },
});
